import React, { Fragment, useEffect, useState } from 'react';
import { Card, Col, Row, Spinner as ReactSpinner } from 'react-bootstrap';
import TransactionAttributes from './TransactionAttributes';
import { transactionApi } from '../apis';
import { InternalApiEntityType, InternalApiTransaction } from '../api-internal';
import RelatedPersonLink from './RelatedPersonLink';
import './LinksToRelatedPersons.scss';
import { LinksToRelatedPersons } from './LinksToRelatedPersons';
import { lengthOrZero } from '../common/helper';
import {
  CAN_MODIFY_ALERT_THROUGH_FILTER,
  CAN_READ_ALERT,
  CAN_READ_CASE,
  CAN_READ_ENTITY_GROUP,
  CAN_READ_SANCTION_ALERT,
} from '../auth/permissions';
import Has from '../auth/Has';
import { TransactionAlerts } from '../aml/sections/TransactionAlerts';
import TransactionScreeningAlerts from '../aml/sections/TransactionScreeningAlerts';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import TransactionBridgeAlerts from '../aml/sections/TransactionBridgeAlerts';
import TransactionManualAlerts from '../aml/sections/TransactionManualAlerts';
import { useParams } from 'react-router';
import { useMe } from '../organisation/useMe';
import SalvPage from '../common/SalvPage';
import MatchingSegments from '../aml/sections/MatchingSegments';
import AttachmentCases from '../case/attachments/AttachmentCases';
import { InternalApiCaseAttachmentType } from '../api-internal';

export const Transaction = () => {
  const id = useParams<{ id: string }>().id!;
  const [fetchingTransaction, setFetchingTransaction] = useState(true);
  const [transaction, setTransaction] = useState<InternalApiTransaction>();
  const { features, currentOrganisation } = useMe();

  useEffect(() => {
    transactionApi
      .findTransactionById({ transactionId: id })
      .then((transaction) => {
        setTransaction(transaction);
      })
      .catch(() => {
        console.warn(`Transaction with ID ${id} not found.`);
      })
      .finally(() => {
        setFetchingTransaction(false);
      });
  }, [id]);

  const renderNoTransaction = () => (
    <Card>
      <Card.Body>
        <div className='small'>Could not find transaction with ID: {id}.</div>
      </Card.Body>
    </Card>
  );

  const renderTransaction = (transaction: InternalApiTransaction) => (
    <Fragment>
      <Row>
        <Col md={4}>
          <TransactionAttributes
            transaction={transaction}
            showLinkToTransactionPage={false}
            showLinkToCustomerPage={false}
          />
        </Col>
        <Col md={8}>
          <Has allPermissions={CAN_READ_ENTITY_GROUP}>
            <MatchingSegments entityId={transaction.id} entityType={InternalApiEntityType.TRANSACTION} />
          </Has>
          {isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, features) && (
            <Has anyPermissions={[CAN_READ_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
              <TransactionAlerts personId={transaction.customer?.personId || ''} transactionId={id} />
            </Has>
          )}
          {isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, features) && (
            <Has anyPermissions={[CAN_READ_SANCTION_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
              <TransactionScreeningAlerts transactionId={id} />
            </Has>
          )}
          {isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features) && (
            <TransactionBridgeAlerts transactionId={id} />
          )}
          <Has anyPermissions={[CAN_READ_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
            <TransactionManualAlerts
              transactionId={id}
              personId={transaction.customer?.personId}
              personName={transaction.customer?.displayName}
              transactionAmount={transaction.attributes?.amount}
            />
          </Has>
          {isFeatureEnabled(ORGANISATION_FEATURE.CASE_MANAGEMENT, features) && (
            <Has allPermissions={[CAN_READ_CASE]}>
              <AttachmentCases attachmentType={InternalApiCaseAttachmentType.TRANSACTION} externalId={id} />
            </Has>
          )}
        </Col>
      </Row>
    </Fragment>
  );

  return (
    <SalvPage
      title={`Transaction ${id}`}
      subTitle={
        transaction && (
          <div>
            {lengthOrZero(transaction.relatedPersons) === 0 && transaction.customer && (
              <RelatedPersonLink {...transaction.customer} />
            )}
            {lengthOrZero(transaction.relatedPersons) === 1 && <RelatedPersonLink {...transaction.relatedPersons[0]} />}
            {lengthOrZero(transaction.relatedPersons) >= 2 && (
              <LinksToRelatedPersons
                transaction={transaction}
                currency={currentOrganisation?.currency}
                includeLinkToTrx={false}
              />
            )}
          </div>
        )
      }
    >
      {!transaction ? (
        fetchingTransaction ? (
          <Card>
            <Card.Body>
              <div className='small'>Loading...</div>
              <ReactSpinner className='loader-spinner' animation='border' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </ReactSpinner>
            </Card.Body>
          </Card>
        ) : (
          renderNoTransaction()
        )
      ) : (
        renderTransaction(transaction)
      )}
    </SalvPage>
  );
};

export default Transaction;
